import jsPDF from 'jspdf';
import { createRoot, type Root } from 'react-dom/client';
import React from 'react';
import type { PostureAnalysis, Client } from '@/lib/types';
import type { CaptureSequence } from '@/components/posture-analysis/types';
import SelfAssessmentReport from '@/components/posture-analysis/self-assessment/SelfAssessmentReport';
import { appendPostureAnalysisToPdf } from '@/lib/pdf-export';
import { appendHtmlElementToPdf } from '@/lib/html-to-pdf';
import {
  buildSelfAssessmentPdfFilename,
  buildSelfAssessmentReportSections,
} from './self-assessment-report-content';

export interface SelfAssessmentPdfOptions {
  contactName?: string;
  contactEmail?: string;
  hideExercises?: boolean;
}

const PDF_CAPTURE_WIDTH_PX = 768;

/** Let React commit and the browser paint before html2canvas measures layout. */
async function waitForReportRender(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

async function renderReportForCapture(
  analysis: PostureAnalysis,
  options?: SelfAssessmentPdfOptions,
): Promise<{ container: HTMLDivElement; root: Root }> {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = `${PDF_CAPTURE_WIDTH_PX}px`;
  container.style.background = '#fcfcfc';
  document.body.appendChild(container);

  const root = createRoot(container);
  // Self-assessment-simple and the full SA flow export this branded light PDF.
  // Do not pass followAppTheme — that token path is only for in-app sales/admin.
  root.render(
    React.createElement(SelfAssessmentReport, {
      analysis,
      detailsUnlocked: true,
      variant: 'pdf',
      contactName: options?.contactName,
      hideExercises: options?.hideExercises ?? true,
    }),
  );

  await waitForReportRender();

  return { container, root };
}

function hasPostureAnalysisPhotoContent(analysis: PostureAnalysis): boolean {
  return ['Anterior', 'Left', 'Right', 'Posterior'].some((view) => {
    const c = analysis.captured[view as CaptureSequence];
    return c?.imageData && c?.landmarks && c?.cmPerPx !== undefined;
  });
}

function hasExportableContent(analysis: PostureAnalysis): boolean {
  const hasPhoto = ['Anterior', 'Left', 'Right', 'Posterior'].some((view) => {
    const c = analysis.captured?.[view as CaptureSequence];
    return c?.imageData && c?.landmarks;
  });
  const hasPattern = !!(analysis.classification?.pattern || analysis.classification?.overallPattern);
  return hasPhoto || hasPattern;
}

export async function exportSelfAssessmentReportToPdf(
  analysis: PostureAnalysis,
  options?: SelfAssessmentPdfOptions,
): Promise<void> {
  if (!hasExportableContent(analysis)) {
    throw new Error('No analysis content available to export.');
  }

  let container: HTMLDivElement | null = null;
  let root: Root | null = null;

  try {
    const rendered = await renderReportForCapture(analysis, options);
    container = rendered.container;
    root = rendered.root;

    const captureTarget =
      container.querySelector<HTMLElement>('[data-testid="self-assessment-report-pdf-root"]') ?? container;

    const doc = new jsPDF();
    await appendHtmlElementToPdf(doc, captureTarget);

    if (hasPostureAnalysisPhotoContent(analysis)) {
      const pseudoClient: Client | null = options?.contactName?.trim()
        ? ({ name: options.contactName.trim() } as Client)
        : null;

      // Force a page break so vector photo headers never overlap the html2canvas report capture.
      doc.addPage();

      await appendPostureAnalysisToPdf(doc, analysis, pseudoClient, {
        title: 'Your Posture Photos & Measurements',
        laymanMode: true,
        startOnNewPage: false,
      });
    }

    const created =
      analysis.createdAt && typeof (analysis.createdAt as { toDate?: () => Date }).toDate === 'function'
        ? (analysis.createdAt as { toDate: () => Date }).toDate()
        : analysis.createdAt instanceof Date
          ? analysis.createdAt
          : new Date();

    doc.save(buildSelfAssessmentPdfFilename(options?.contactName, created));
  } finally {
    root?.unmount();
    container?.remove();
  }
}
